import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Progress } from '../../components/ui/progress';
import api from '../../services/api';
import { toast } from 'sonner';
import { Upload, Download, FileSpreadsheet, CheckCircle, AlertCircle, Loader2, Users } from 'lucide-react';

const StudentImportPage = () => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const handleDownloadTemplate = async () => {
    try {
      const response = await api.get('/students/export/template', {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'student_import_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Template downloaded');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (!selectedFile.name.match(/\.(xlsx|xls|csv)$/)) {
        toast.error('Please select an Excel or CSV file');
        return;
      }
      setFile(selectedFile);
      setResult(null);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error('Please select a file first');
      return;
    }

    setUploading(true);
    setResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post('/students/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setResult(response.data);
      
      if (response.data.success_count > 0) {
        toast.success(`Successfully imported ${response.data.success_count} students`);
      }
      if (response.data.error_count > 0) {
        toast.warning(`${response.data.error_count} errors occurred`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Import failed');
      setResult({ error: error.response?.data?.detail || 'Import failed' });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="student-import-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-heading font-bold">Import Students</h1>
        <p className="text-muted-foreground mt-1">Bulk import students from Excel or CSV file</p>
      </div>

      {/* Instructions */}
      <Card className="border border-border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5 text-primary" />
            Import Instructions
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-medium mb-2">Required Columns:</h4>
              <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
                <li><span className="font-medium text-foreground">name</span> - Student's full name</li>
                <li><span className="font-medium text-foreground">roll_number</span> - Unique roll number</li>
                <li><span className="font-medium text-foreground">class_id</span> - Class identifier (e.g., class-1)</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-2">Optional Columns:</h4>
              <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
                <li><span className="font-medium text-foreground">email</span> - Student email (auto-generated if empty)</li>
                <li><span className="font-medium text-foreground">parent_email</span> - Parent's email for login</li>
                <li><span className="font-medium text-foreground">phone</span> - Contact number</li>
                <li><span className="font-medium text-foreground">gender</span> - male/female/other</li>
                <li><span className="font-medium text-foreground">date_of_birth</span> - YYYY-MM-DD format</li>
                <li><span className="font-medium text-foreground">address</span> - Home address</li>
              </ul>
            </div>
          </div>

          <div className="flex gap-2">
            <Button variant="outline" onClick={handleDownloadTemplate} data-testid="download-template-button">
              <Download className="h-4 w-4 mr-2" />
              Download Template
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Upload Section */}
      <Card className="border border-border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5 text-primary" />
            Upload File
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="border-2 border-dashed border-border rounded-lg p-8 text-center">
            <FileSpreadsheet className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground mb-4">
              Drag and drop your Excel or CSV file here, or click to browse
            </p>
            <Input
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={handleFileChange}
              className="max-w-xs mx-auto"
              data-testid="import-file-input"
            />
            {file && (
              <div className="mt-4 p-3 bg-muted/50 rounded-lg inline-block">
                <p className="text-sm font-medium">{file.name}</p>
                <p className="text-xs text-muted-foreground">{(file.size / 1024).toFixed(2)} KB</p>
              </div>
            )}
          </div>

          <div className="flex justify-center">
            <Button 
              onClick={handleUpload} 
              disabled={!file || uploading}
              size="lg"
              data-testid="import-submit-button"
            >
              {uploading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Importing...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Import Students
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      {result && (
        <Card className="border border-border">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {result.error ? (
                <AlertCircle className="h-5 w-5 text-red-500" />
              ) : (
                <CheckCircle className="h-5 w-5 text-green-500" />
              )}
              Import Results
            </CardTitle>
          </CardHeader>
          <CardContent>
            {result.error ? (
              <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
                <p className="text-red-600 dark:text-red-400">{result.error}</p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-lg text-center">
                    <p className="text-3xl font-bold text-green-500">{result.success_count || 0}</p>
                    <p className="text-sm text-muted-foreground">Successfully Imported</p>
                  </div>
                  <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-center">
                    <p className="text-3xl font-bold text-red-500">{result.error_count || 0}</p>
                    <p className="text-sm text-muted-foreground">Errors</p>
                  </div>
                  <div className="p-4 bg-primary/10 border border-primary/20 rounded-lg text-center">
                    <p className="text-3xl font-bold text-primary">{(result.success_count || 0) + (result.error_count || 0)}</p>
                    <p className="text-sm text-muted-foreground">Total Rows</p>
                  </div>
                </div>

                {result.errors && result.errors.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="font-medium text-red-500">Errors:</h4>
                    <div className="max-h-48 overflow-y-auto bg-muted/50 rounded-lg p-3">
                      {result.errors.map((err, idx) => (
                        <p key={idx} className="text-sm text-muted-foreground">{err}</p>
                      ))}
                    </div>
                  </div>
                )}

                <div className="p-4 bg-muted/50 rounded-lg">
                  <p className="text-sm text-muted-foreground">
                    <Users className="h-4 w-4 inline mr-2" />
                    Student accounts have been created with default password: <span className="font-mono font-medium">student123</span>
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Parent accounts (if parent_email provided) use password: <span className="font-mono font-medium">parent123</span>
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Tips */}
      <Card className="border border-border bg-primary/5">
        <CardContent className="p-4">
          <h4 className="font-medium mb-2">Tips for successful import:</h4>
          <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
            <li>Ensure all required columns (name, roll_number, class_id) are present</li>
            <li>Class IDs should match existing classes (e.g., class-1, class-2, class-3)</li>
            <li>Roll numbers must be unique across all students</li>
            <li>For 420 students, the import may take 1-2 minutes</li>
            <li>Duplicate entries (same email or roll number) will be skipped</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export default StudentImportPage;
